import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Search,
  Shield,
  AlertTriangle,
  CheckCircle,
  Info,
  Heart,
  Sparkles,
  Filter,
  Dog,
  Cat,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AllergenGenetics = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('breeds');
  const [loading, setLoading] = useState(false);
  
  // Breed profiles state
  const [breedProfiles, setBreedProfiles] = useState([]);
  const [petTypeFilter, setPetTypeFilter] = useState('dog');
  const [hypoallergenicOnly, setHypoallergenicOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Recommendations state
  const [allergySeverity, setAllergySeverity] = useState('moderate');
  const [hasAsthma, setHasAsthma] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  
  // Compatibility check state
  const [compatibilityDialogOpen, setCompatibilityDialogOpen] = useState(false);
  const [selectedPetId, setSelectedPetId] = useState('');
  const [compatibilityResult, setCompatibilityResult] = useState(null);
  const [userPets, setUserPets] = useState([]);
  
  // Selected breed detail
  const [selectedBreed, setSelectedBreed] = useState(null);

  useEffect(() => {
    fetchBreedProfiles();
  }, [petTypeFilter, hypoallergenicOnly]);

  useEffect(() => {
    if (user) {
      fetchUserPets();
    }
  }, [user]);

  const fetchBreedProfiles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('pet_type', petTypeFilter);
      if (hypoallergenicOnly) params.append('hypoallergenic_only', 'true');
      
      const response = await axios.get(`${API}/allergens/breeds?${params}`);
      setBreedProfiles(response.data);
    } catch (error) {
      console.error('Error fetching breed profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPets = async () => {
    try {
      const response = await axios.get(`${API}/pets/me`);
      setUserPets(response.data);
    } catch (error) {
      console.error('Error fetching pets:', error);
    }
  };

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('allergy_severity', allergySeverity);
      params.append('pet_type', petTypeFilter);
      params.append('has_asthma', hasAsthma.toString());
      
      const response = await axios.get(`${API}/allergens/recommendations?${params}`);
      setRecommendations(response.data);
    } catch (error) {
      toast.error('Failed to fetch recommendations');
    } finally {
      setLoading(false);
    }
  };

  const checkCompatibility = async () => {
    if (!selectedPetId) {
      toast.error('Please select a pet');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/allergens/compatibility/${selectedPetId}`, {
        allergy_severity: allergySeverity,
        has_asthma: hasAsthma
      });
      setCompatibilityResult(response.data);
    } catch (error) {
      toast.error('Failed to check compatibility');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-100';
    if (score >= 5) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getLevelColor = (level) => {
    const colors = {
      'very_low': 'bg-green-100 text-green-700',
      'low': 'bg-green-50 text-green-600',
      'moderate': 'bg-yellow-100 text-yellow-700',
      'high': 'bg-orange-100 text-orange-700',
      'very_high': 'bg-red-100 text-red-700'
    };
    return colors[level] || 'bg-gray-100 text-gray-700';
  };

  const filteredBreeds = breedProfiles.filter(breed =>
    breed.breed_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold mb-2 flex items-center gap-3">
          <Shield className="w-8 h-8 text-primary" />
          Allergen Genetics Center
        </h1>
        <p className="text-muted-foreground">
          Find hypoallergenic breeds, check compatibility, and manage pet allergies
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex">
          <TabsTrigger value="breeds" data-testid="tab-breeds">
            <Dog className="w-4 h-4 mr-2" />
            Breed Profiles
          </TabsTrigger>
          <TabsTrigger value="recommendations" data-testid="tab-recommendations">
            <Sparkles className="w-4 h-4 mr-2" />
            Get Recommendations
          </TabsTrigger>
          <TabsTrigger value="compatibility" data-testid="tab-compatibility">
            <Heart className="w-4 h-4 mr-2" />
            Compatibility Check
          </TabsTrigger>
        </TabsList>

        {/* Breed Profiles Tab */}
        <TabsContent value="breeds" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-muted-foreground" />
                  <Select value={petTypeFilter} onValueChange={setPetTypeFilter}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dog">Dogs</SelectItem>
                      <SelectItem value="cat">Cats</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search breeds..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                
                <Button
                  variant={hypoallergenicOnly ? "default" : "outline"}
                  onClick={() => setHypoallergenicOnly(!hypoallergenicOnly)}
                  className="gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Hypoallergenic Only
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Breed Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredBreeds.map((breed) => (
                <Card 
                  key={breed.id} 
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => setSelectedBreed(breed)}
                  data-testid={`breed-card-${breed.breed_name.toLowerCase().replace(' ', '-')}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{breed.breed_name}</CardTitle>
                        <CardDescription className="capitalize">{breed.pet_type}</CardDescription>
                      </div>
                      <Badge className={getScoreColor(breed.hypoallergenic_score)}>
                        Score: {breed.hypoallergenic_score}/10
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className={getLevelColor(breed.dander_level)}>
                          Dander: {breed.dander_level.replace('_', ' ')}
                        </Badge>
                        <Badge variant="outline" className={getLevelColor(breed.shedding_level)}>
                          Shedding: {breed.shedding_level.replace('_', ' ')}
                        </Badge>
                      </div>
                      
                      <div className="flex gap-2">
                        {breed.good_for_allergies && (
                          <Badge className="bg-green-500">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Allergy Friendly
                          </Badge>
                        )}
                        {breed.recommended_for_severe_allergies && (
                          <Badge className="bg-blue-500">
                            <Shield className="w-3 h-3 mr-1" />
                            Severe Allergies OK
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {breed.notes}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {filteredBreeds.length === 0 && !loading && (
            <div className="text-center py-12 text-muted-foreground">
              <Dog className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No breeds found matching your criteria</p>
            </div>
          )}
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Get Personalized Recommendations</CardTitle>
              <CardDescription>
                Tell us about your allergies and we'll suggest the best breeds for you
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Pet Type</label>
                  <Select value={petTypeFilter} onValueChange={setPetTypeFilter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dog">Dog</SelectItem>
                      <SelectItem value="cat">Cat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Allergy Severity</label>
                  <Select value={allergySeverity} onValueChange={setAllergySeverity}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mild">Mild - Occasional sneezing</SelectItem>
                      <SelectItem value="moderate">Moderate - Regular symptoms</SelectItem>
                      <SelectItem value="severe">Severe - Strong reactions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Do you have asthma?</label>
                  <Select value={hasAsthma ? 'yes' : 'no'} onValueChange={(v) => setHasAsthma(v === 'yes')}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button onClick={fetchRecommendations} disabled={loading} className="w-full md:w-auto">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Get Recommendations
              </Button>
            </CardContent>
          </Card>

          {recommendations && (
            <>
              {/* Tips Card */}
              <Card className="border-blue-200 bg-blue-50/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Info className="w-5 h-5 text-blue-600" />
                    Allergy Management Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {recommendations.allergy_management_tips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Recommended Breeds */}
              <div>
                <h3 className="text-lg font-semibold mb-4">
                  Recommended Breeds (Score ≥ {recommendations.minimum_score_recommended})
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {recommendations.recommended_breeds.map((breed) => (
                    <Card key={breed.id} className="border-green-200">
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium">{breed.breed_name}</h4>
                          <Badge className={getScoreColor(breed.hypoallergenic_score)}>
                            {breed.hypoallergenic_score}/10
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{breed.notes}</p>
                        <div className="flex flex-wrap gap-1">
                          {breed.recommended_for_severe_allergies && (
                            <Badge variant="outline" className="text-xs bg-green-50">
                              Safe for severe allergies
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* Compatibility Check Tab */}
        <TabsContent value="compatibility" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Check Pet Compatibility</CardTitle>
              <CardDescription>
                Check if a specific pet is compatible with your allergy profile
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Your Allergy Severity</label>
                  <Select value={allergySeverity} onValueChange={setAllergySeverity}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mild">Mild</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="severe">Severe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Do you have asthma?</label>
                  <Select value={hasAsthma ? 'yes' : 'no'} onValueChange={(v) => setHasAsthma(v === 'yes')}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {user ? (
                <div>
                  <label className="text-sm font-medium mb-2 block">Select a Pet to Check</label>
                  <Select value={selectedPetId} onValueChange={setSelectedPetId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a pet..." />
                    </SelectTrigger>
                    <SelectContent>
                      {userPets.map((pet) => (
                        <SelectItem key={pet.id} value={pet.id}>
                          {pet.name} ({pet.breed || pet.pet_type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button 
                    onClick={checkCompatibility} 
                    disabled={loading || !selectedPetId}
                    className="mt-4"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Heart className="w-4 h-4 mr-2" />}
                    Check Compatibility
                  </Button>
                </div>
              ) : (
                <div className="text-center py-6 bg-muted/50 rounded-lg">
                  <p className="text-muted-foreground">Please log in to check compatibility with your pets</p>
                </div>
              )}
            </CardContent>
          </Card>

          {compatibilityResult && (
            <Card className={
              compatibilityResult.recommendation === 'highly_recommended' ? 'border-green-300 bg-green-50/50' :
              compatibilityResult.recommendation === 'recommended' ? 'border-blue-300 bg-blue-50/50' :
              compatibilityResult.recommendation === 'caution' ? 'border-yellow-300 bg-yellow-50/50' :
              'border-red-300 bg-red-50/50'
            }>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Compatibility Result: {compatibilityResult.pet_name}</span>
                  <Badge className={
                    compatibilityResult.compatibility_score >= 70 ? 'bg-green-500' :
                    compatibilityResult.compatibility_score >= 50 ? 'bg-yellow-500' :
                    'bg-red-500'
                  }>
                    {compatibilityResult.compatibility_score}% Compatible
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  {compatibilityResult.recommendation === 'highly_recommended' && (
                    <Badge className="bg-green-500">Highly Recommended</Badge>
                  )}
                  {compatibilityResult.recommendation === 'recommended' && (
                    <Badge className="bg-blue-500">Recommended</Badge>
                  )}
                  {compatibilityResult.recommendation === 'caution' && (
                    <Badge className="bg-yellow-500">Proceed with Caution</Badge>
                  )}
                  {compatibilityResult.recommendation === 'not_recommended' && (
                    <Badge className="bg-red-500">Not Recommended</Badge>
                  )}
                </div>

                {compatibilityResult.warnings.length > 0 && (
                  <div className="bg-yellow-100 rounded-lg p-3">
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-600" />
                      Warnings
                    </h4>
                    <ul className="text-sm space-y-1">
                      {compatibilityResult.warnings.map((warning, idx) => (
                        <li key={idx}>• {warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <h4 className="font-medium mb-2">Management Tips</h4>
                  <ul className="text-sm space-y-1">
                    {compatibilityResult.management_tips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Recommended Preparations</h4>
                  <ul className="text-sm space-y-1">
                    {compatibilityResult.recommended_preparations.map((prep, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                        {prep}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Breed Detail Dialog */}
      <Dialog open={!!selectedBreed} onOpenChange={() => setSelectedBreed(null)}>
        <DialogContent className="max-w-2xl">
          {selectedBreed && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>{selectedBreed.breed_name}</span>
                  <Badge className={getScoreColor(selectedBreed.hypoallergenic_score)}>
                    Hypoallergenic Score: {selectedBreed.hypoallergenic_score}/10
                  </Badge>
                </DialogTitle>
                <DialogDescription>{selectedBreed.notes}</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-2">Allergen Levels</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Dander:</span>
                        <Badge className={getLevelColor(selectedBreed.dander_level)}>
                          {selectedBreed.dander_level.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Saliva:</span>
                        <Badge className={getLevelColor(selectedBreed.saliva_level)}>
                          {selectedBreed.saliva_level.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Shedding:</span>
                        <Badge className={getLevelColor(selectedBreed.shedding_level)}>
                          {selectedBreed.shedding_level.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-2">Coat Information</h4>
                    <div className="text-sm space-y-1">
                      <p>Type: <span className="capitalize">{selectedBreed.coat_type}</span></p>
                      <p>Seasonal Shedding: {selectedBreed.sheds_seasonally ? 'Yes' : 'No'}</p>
                      {selectedBreed.produces_less_fel_d1 && (
                        <Badge className="bg-green-100 text-green-700">
                          Produces less Fel d 1 protein
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {selectedBreed.grooming_tips.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Grooming Tips</h4>
                    <ul className="text-sm space-y-1">
                      {selectedBreed.grooming_tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-primary">•</span> {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedBreed.allergy_management_tips.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Allergy Management Tips</h4>
                    <ul className="text-sm space-y-1">
                      {selectedBreed.allergy_management_tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedBreed.allergen_proteins.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Known Allergen Proteins</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedBreed.allergen_proteins.map((protein, idx) => (
                        <Badge key={idx} variant="outline">{protein}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllergenGenetics;
